/**
 * AD-007n change 4: **the keyboard is a stick, not a switch.**
 *
 * `RB.EXE`'s keyboard control is a ramped analogue axis, and nobody had measured
 * it until the flight-feel session did (`re-sessions/feel/NOTES.md` §4,
 * `docs/engineering/flight-feel-findings.md` §3). Held, the original's aileron
 * takes about two seconds to reach full deflection; released, it keeps rolling
 * for about another second and then stops. The port's keyboard was bang-bang,
 * so every input was a full-deflection input:
 *
 * | | original (Camel, right aileron) | port | ratio |
 * |---|---:|---:|---:|
 * | roll rate 0.3 s after the key goes down | 8.3 °/s | 46.2 °/s | 5.6× |
 * | bank at 0.6 s | 4.8° | 24.7° | 5.1× |
 * | time to 30° of bank | 1.56 s | 0.72 s | 0.46× |
 * | time to full roll rate | ≈2.1 s | 0.30 s | 0.14× |
 * | bank after a 0.6 s tap, at release | 6.9° | 24.6° | 3.6× |
 * | …and after the roll-out tail (+1.0 s) | 14.1° | 29.7° | 2.1× |
 *
 * What the ramp buys is **proportional control from a keyboard**: tap length
 * maps smoothly onto bank angle and onto how hard you are pulling. Without it a
 * player who wants a gentle turn has no way to ask for one — there are exactly
 * two elevator positions, neutral and maximum, and maximum is the manoeuvre
 * AD-007n's other three changes exist to make survivable.
 *
 * **This is a genuine choice, so it is an input option and not a flight-model
 * tier.** The ramp ships as the default because it is both authentic and the
 * thing that makes a gentle input expressible; "instant" stays available for
 * players who want a modern keyboard's immediacy. It must never be folded into
 * NOVICE/INTERMEDIATE/EXPERT, which are the original's own three-way and are
 * already spoken for (AD-007b). An analogue axis — gamepad, mouse-stick — gets
 * its deflection immediately either way: the ramp is a keyboard's substitute for
 * a hand on a stick, not a handling characteristic of the aeroplane.
 *
 * **It lives in the engine rather than in the app** because it is a pure,
 * deterministic function of held keys and elapsed time, because the calibration
 * rails have to fly it (the original's bank *creeps* after the aileron is
 * released, and that tail is the reason `PULL1`'s bank runs 26° → 37° → 48°
 * rather than sitting where it was let go), and because a second port surface —
 * the AI, a replay, a duel peer — must be able to produce the same stick from
 * the same keys. `step` still takes raw `FlightInputs`: this shapes what is
 * handed to it and changes nothing about the integrator's contract.
 */

import { clamp } from "./units.ts";

/** One frame's stick position on the three control axes, −1…+1. */
export interface StickAxes {
  readonly pitch: number;
  readonly roll: number;
  readonly yaw: number;
}

export const CENTRED_STICK: StickAxes = { pitch: 0, roll: 0, yaw: 0 };

export interface StickShaping {
  /** Seconds from centred to full deflection with the key held. Zero is bang-bang. */
  readonly rampSeconds: number;
  /** Seconds from full deflection back to centred once the key is let go. */
  readonly releaseSeconds: number;
}

/**
 * The measured shape. **1.9 s** is a fit rather than a transcription: the
 * session's own anchors are 4.8° of bank at 0.6 s, 30° at 1.56 s and 45° at
 * 2.02 s of held aileron, and a linear rate ramp reaching full deflection at
 * 1.9 s lands all three inside 10 %, where the trace's own "time to full roll
 * rate ≈2.1 s" is where it *looks* flat and would under-produce the sub-second
 * anchors by 25 %. The two readings agree to the resolution the ramp was
 * measured at.
 *
 * **1.0 s** is read directly: a 0.6 s tap keeps rolling for a further second and
 * doubles the bank it had at release (6.9° → 14.1°), then stops.
 */
export const AUTHENTIC_STICK_SHAPING: StickShaping = { rampSeconds: 1.55, releaseSeconds: 1.0 };

/** The port's previous behaviour, kept as the named alternative the option offers. */
export const INSTANT_STICK_SHAPING: StickShaping = { rampSeconds: 0, releaseSeconds: 0 };

export type StickShapingId = "authentic" | "instant";

export function stickShapingFor(id: StickShapingId): StickShaping {
  return id === "instant" ? INSTANT_STICK_SHAPING : AUTHENTIC_STICK_SHAPING;
}

/**
 * Advances one axis toward `demand` at the shaping's own rate. Deflecting
 * *toward* a held key uses `rampSeconds`; returning toward centre — because the
 * key was let go, or because the opposite key is now down and the stick has to
 * cross zero — uses `releaseSeconds`, which is the faster of the two and is what
 * makes the roll-out tail a tail rather than a second full-length ramp.
 */
function advanceAxis(current: number, demand: number, dt: number, shaping: StickShaping): number {
  const target = clamp(demand, -1, 1);
  if (target === current) return current;
  const towardCentre = Math.abs(target) < Math.abs(current) || target * current < 0;
  const seconds = towardCentre ? shaping.releaseSeconds : shaping.rampSeconds;
  if (seconds <= 0) return target;
  const stepSize = dt / seconds;
  const delta = target - current;
  return Math.abs(delta) <= stepSize ? target : current + Math.sign(delta) * stepSize;
}

/** One frame of stick travel: where the axes were, where the keys are asking them to be. */
export function advanceStick(
  current: StickAxes,
  demand: StickAxes,
  dt: number,
  shaping: StickShaping = AUTHENTIC_STICK_SHAPING,
): StickAxes {
  return {
    pitch: advanceAxis(current.pitch, demand.pitch, dt, shaping),
    roll: advanceAxis(current.roll, demand.roll, dt, shaping),
    yaw: advanceAxis(current.yaw, demand.yaw, dt, shaping),
  };
}
